/**
 * Fit/Intent banding, GTM action labels, and heatmap colors for the scoring
 * matrix. Bands mirror the ScoreChip tiers; thresholds are user-adjustable.
 */

export type Band = "high" | "mid" | "low";
export type ActionLabel = "Prioritize" | "Nurture" | "Monitor" | "Dequeue";

export interface MatrixThresholds {
  /** A score >= `high` is the High band. */
  high: number;
  /** A score >= `mid` (and < `high`) is the Mid band; below `mid` is Low. */
  mid: number;
}

// Mid band spans 40–70 (Mid ≥ 40, High ≥ 70); Low is below 40.
export const DEFAULT_THRESHOLDS: MatrixThresholds = { high: 70, mid: 40 };

/** Matrix layout: Fit rows top→bottom, Intent columns left→right. */
export const FIT_BANDS: Band[] = ["high", "mid", "low"];
export const INTENT_BANDS: Band[] = ["low", "mid", "high"];

export const BAND_LABEL: Record<Band, string> = {
  high: "High",
  mid: "Mid",
  low: "Low",
};

export function bandForScore(score: number, t: MatrixThresholds): Band {
  if (score >= t.high) return "high";
  if (score >= t.mid) return "mid";
  return "low";
}

/**
 * Min/max (0–100) score range for a band, given thresholds. `max` is omitted
 * for High and `min` for Low so the ranges are open-ended at the extremes.
 */
export function bandRange(
  band: Band,
  t: MatrixThresholds
): { min?: number; max?: number } {
  if (band === "high") return { min: t.high };
  if (band === "mid") return { min: t.mid, max: t.high - 1 };
  return { max: t.mid - 1 };
}

const RANK: Record<Band, number> = { low: 0, mid: 1, high: 2 };

/** Combined rank 0–4 (low/low → 0 … high/high → 4); drives the heatmap color. */
export function cellRank(fit: Band, intent: Band): number {
  return RANK[fit] + RANK[intent];
}

/** GTM action label per cell — matches the product mockup exactly. */
export function actionLabelFor(fit: Band, intent: Band): ActionLabel {
  if (fit === "high" && intent === "high") return "Prioritize";
  if (fit === "low" && intent === "low") return "Dequeue";
  if (fit === "high") return "Nurture"; // high/mid, high/low
  if (fit === "mid" && intent === "high") return "Nurture";
  return "Monitor"; // mid/mid, mid/low, low/mid, low/high
}

/** Heatmap background by combined rank 0 (worst, red) → 4 (best, green). */
const CELL_BG = ["#F4A79F", "#FBD9D9", "#FCF4D6", "#D2EFDC", "#7CD7A0"];

export function cellBackground(fit: Band, intent: Band): string {
  return CELL_BG[cellRank(fit, intent)];
}

/** Compare-view delta colors (A − B). */
export function deltaBackground(delta: number): string {
  if (delta > 0) return "#D9F0E0";
  if (delta < 0) return "#FBDAD7";
  return "#EEF1F4";
}

export function deltaTextColor(delta: number): string {
  if (delta > 0) return "#2E7D32";
  if (delta < 0) return "#C62828";
  return "#90A4AE";
}
